/**
 * The small digits (ADR-061's amendment "the player card", the author's ruling of 2026-09-27 that the health number
 * be smaller and sit wholly inside its bar): 0 to 9, the slash and the percent sign (the target frame's Health
 * percentage, P2.2-T3) and the plus sign (a tab badge's "9+", ADR-068's amendment "scope tabs, and Help's icon")
 * drawn 3 by 5 pixels in the kit's pixel style, one pixel apart, with a one-pixel dark outline that is closed across each row, so no fill shows through between the
 * glyphs inside the number. The pixel face's smallest role has 7-row capitals; these hold a value in a bar 9 pixels
 * tall. They are plain cell masks, drawn in the DOM as an SVG path (`maskPath`) and usable by a canvas bar alike.
 */

/**
 * The glyphs, row by row from the top, `1` for a set pixel.
 */
export const SMALL_DIGIT_GLYPHS: Readonly<Record<string, readonly string[]>> = {
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  "2": ["111", "001", "111", "100", "111"],
  "3": ["111", "001", "011", "001", "111"],
  "4": ["101", "101", "111", "001", "001"],
  "5": ["111", "100", "111", "001", "111"],
  "6": ["111", "100", "111", "101", "111"],
  "7": ["111", "001", "010", "010", "010"],
  "8": ["111", "101", "111", "101", "111"],
  "9": ["111", "101", "111", "001", "111"],
  "/": ["001", "001", "010", "100", "100"],
  "%": ["101", "001", "010", "100", "101"],
  "+": ["000", "010", "111", "010", "000"],
};

/**
 * A glyph's size, the distance from one glyph's left to the next's, and the outline's width, in logical pixels.
 */
export const SMALL_DIGITS = { width: 3, height: 5, advance: 4, outline: 1 } as const;

/**
 * A text drawn in the small digits: its box (the text and the outline on every side), the glyphs' cells and the
 * outline's cells, each a mask of rows from the box's top left.
 */
export interface SmallDigits {
  readonly width: number;
  readonly height: number;
  readonly glyph: readonly (readonly boolean[])[];
  readonly outline: readonly (readonly boolean[])[];
}

/**
 * The width of a text in the small digits, without its outline.
 *
 * @param text The text.
 * @returns The width in logical pixels: the advance a character less the gap after the last, 0 for no text.
 * @example
 * smallDigitsWidth("60/100");
 * // 23
 */
export function smallDigitsWidth(text: string): number {
  return text.length === 0 ? 0 : text.length * SMALL_DIGITS.advance - (SMALL_DIGITS.advance - SMALL_DIGITS.width);
}

/**
 * Whether the small digits hold every character of a text, so `smallDigits` draws it rather than throwing.
 *
 * @param text The text.
 * @returns True for a text of digits, slashes, percent and plus signs, false for an empty text or any other character.
 * @example
 * fitsSmallDigits("0");
 * // true
 * fitsSmallDigits("Q");
 * // false
 */
export function fitsSmallDigits(text: string): boolean {
  return text.length > 0 && [...text].every((character) => Object.hasOwn(SMALL_DIGIT_GLYPHS, character));
}

/**
 * Lays a text out in the small digits: each glyph at its advance inside the outline's margin, and the outline every
 * cell within one pixel of a glyph cell, closed across each row from its first cell to its last.
 *
 * @param text The text: digits, slashes and percent signs.
 * @returns The box and its two masks.
 * @throws RangeError when the text holds a character the set lacks.
 * @example
 * smallDigits("60/100").width;
 * // 25
 */
export function smallDigits(text: string): SmallDigits {
  const edge = SMALL_DIGITS.outline;
  const width = smallDigitsWidth(text) + 2 * edge;
  const height = SMALL_DIGITS.height + 2 * edge;
  const glyph = Array.from({ length: height }, () => new Array<boolean>(width).fill(false));
  [...text].forEach((character, index) => {
    const rows = SMALL_DIGIT_GLYPHS[character];
    if (rows === undefined) {
      throw new RangeError(`the small digits have no glyph for ${JSON.stringify(character)}`);
    }
    rows.forEach((row, y) => {
      [...row].forEach((bit, x) => {
        if (bit === "1") {
          (glyph[edge + y] as boolean[])[edge + index * SMALL_DIGITS.advance + x] = true;
        }
      });
    });
  });
  const outline = glyph.map((_, y) => {
    const near = Array.from({ length: width }, (__, x) => {
      for (let dy = -edge; dy <= edge; dy += 1) {
        for (let dx = -edge; dx <= edge; dx += 1) {
          if (glyph[y + dy]?.[x + dx] === true) {
            return true;
          }
        }
      }
      return false;
    });
    const first = near.indexOf(true);
    const last = near.lastIndexOf(true);
    return near.map((_cell, x) => first !== -1 && x >= first && x <= last);
  });
  return { width, height, glyph, outline };
}

/**
 * An SVG path of a mask's set cells: each row's runs as rectangles one pixel tall, in the mask's own units.
 *
 * @param mask The mask, rows from the top.
 * @returns The path's data, empty for a mask with no set cell.
 * @example
 * maskPath([[true, true, false]]);
 * // "M0 0h2v1h-2z"
 */
export function maskPath(mask: readonly (readonly boolean[])[]): string {
  const runs: string[] = [];
  mask.forEach((row, y) => {
    let start = -1;
    for (let x = 0; x <= row.length; x += 1) {
      const set = row[x] === true;
      if (set && start === -1) {
        start = x;
      } else if (!set && start !== -1) {
        const length = x - start;
        runs.push(`M${start} ${y}h${length}v1h-${length}z`);
        start = -1;
      }
    }
  });
  return runs.join(" ");
}
